import React, { useRef, useState, useEffect } from 'react';
import EQXYCanvas from './EQXYCanvas'
const EQ_LABELS = [
    { key: 'bass', label: '低音ブースト (Bass)' },
    { key: 'treble', label: '高音ブースト (Treble)' }
];

const EQ_COLORS = {
    bass: '#ff00ff',
    treble: '#00ffff'
};

const DEFAULT_PARAMS = {
    VOCAL_GAIN_BIAS_DB: 2.5,
    THRESHOLD_DB: -18,
    RATIO: 3,
    ATTACK_MS: 10,
    RELEASE_MS: 120,
    EQ_UI: {
        bass: { lowcut: 80, highcut: 8000, gain: 0 },
        treble: { lowcut: 200, highcut: 16000, gain: 0 }
    }
};



// ===== Canvas ノブコンポーネント =====
const Knob = ({ min, max, step, value, onChange, size = 80, color, defaultValue = 0 }) => {
    
    const canvasRef = useRef(null);
    const [isDragging, setDragging] = useState(false);

    const drawKnob = (val) => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const c = size / 2;
        const r = size / 2 - 10;

        ctx.clearRect(0, 0, size, size);

        ctx.beginPath();
        ctx.arc(c, c, r, 0, Math.PI * 2);
        ctx.fillStyle = '#333';
        ctx.fill();

        const angle = ((val - min) / (max - min)) * 1.5 * Math.PI + 0.75 * Math.PI;
        
        ctx.beginPath();
        ctx.moveTo(c, c);
        ctx.lineTo(c + r * Math.cos(angle), c + r * Math.sin(angle));
        ctx.strokeStyle = color;
        ctx.lineWidth = 4;
        ctx.stroke();
    };

    useEffect(() => drawKnob(value), [value]);

    const updateValue = (x, y) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const dx = x - rect.left - size / 2;
        const dy = y - rect.top - size / 2;

        let angle = Math.atan2(dy, dx);
        if (angle < 0) angle += Math.PI * 2;

        const start = 0.75 * Math.PI;
        const end = 1.25 * Math.PI;

        angle = Math.max(start, Math.min(end, angle));

        const v = ((angle - start) / (end - start)) * (max - min) + min;
        onChange(Math.round(v / step) * step);
    };

    const down = (x, y) => { setDragging(true); updateValue(x, y); };
    const move = (x, y) => { if (isDragging) updateValue(x, y); };
    const up = () => setDragging(false);

    return (
        <div style={{ textAlign: 'center' }}>
            <canvas
                ref={canvasRef}
                width={size}
                height={size}
                onMouseDown={e => down(e.clientX, e.clientY)}
                onMouseMove={e => move(e.clientX, e.clientY)}
                onMouseUp={up}
                onMouseLeave={up}
                onTouchStart={e => down(e.touches[0].clientX, e.touches[0].clientY)}
                onTouchMove={e => { e.preventDefault(); move(e.touches[0].clientX, e.touches[0].clientY) }}
                onTouchEnd={up}
                style={{ cursor: 'pointer', touchAction: 'none' }}
            />
            <button onClick={() => onChange(defaultValue)}>Reset</button>
        </div>
    );
};


// ===== AudioParamsForm コンポーネント =====
const AudioParamsForm = ({ params, setParams }) => {

    const [eqBands, setEqBands] = useState({
        bass: { freq: 120, gain: 0 },
        treble: { freq: 4000, gain: 0 }
    });
    
    
    /* ===== ノブ → Canvas 同期 ===== */
    useEffect(() => {
        if (
            eqBands.bass.gain !== params.EQ_UI.bass.gain ||
            Math.round(eqBands.bass.freq) !== params.EQ_UI.bass.lowcut ||
            eqBands.treble.gain !== params.EQ_UI.treble.gain ||
            Math.round(eqBands.treble.freq) !== params.EQ_UI.treble.highcut
        ) {
            setParams(prev => ({
                ...prev,
                EQ_UI: {
                    bass: {
                        ...prev.EQ_UI.bass,
                        gain: eqBands.bass.gain,
                        lowcut: Math.round(eqBands.bass.freq)
                    },
                    treble: {
                        ...prev.EQ_UI.treble,
                        gain: eqBands.treble.gain,
                        highcut: Math.round(eqBands.treble.freq)
                    }
                }
            }));
        }
    }, [eqBands]);


    if (!params || !params.EQ_UI) return null;

    
    
    
    
    const update = (key, value) => {
        setParams(prev => ({ ...prev, [key]: value }));
    };

    const updateEQ = (band, key, value) => {
        setParams(prev => ({
            ...prev,
            EQ_UI: {
                ...prev.EQ_UI,
                [band]: { ...prev.EQ_UI[band], [key]: value }
            }
        }));
    };

    return (
        <div style={{
            padding: '16px',
            background: 'rgb(235 235 235)',
            borderRadius: '12px',
            color: '#000000',
            marginTop: '16px',
            fontFamily: 'sans-serif'
        }}>
            <h3>Audio Parameters</h3>

            {/* メインパラメータ */}
            {[
                { label: 'Vocal Gain Bias (dB)', key: 'VOCAL_GAIN_BIAS_DB', min: -24, max: 24, step: 0.1 },
                { label: 'Threshold (dB)', key: 'THRESHOLD_DB', min: -60, max: 0, step: 0.1 },
                { label: 'Ratio', key: 'RATIO', min: 1, max: 20, step: 0.1 },
                { label: 'Attack (ms)', key: 'ATTACK_MS', min: 0, max: 200, step: 1 },
                { label: 'Release (ms)', key: 'RELEASE_MS', min: 0, max: 500, step: 1 },
            ].map(p => (
                <div key={p.key} style={{ marginBottom: '12px' }}>
                    <label>{p.label}: {params[p.key]}</label>
                    <input
                        type="range"
                        min={p.min}
                        max={p.max}
                        step={p.step}
                        value={params[p.key]}
                        onChange={e => update(p.key, +e.target.value)}
                        style={{ width: '100%' }}
                    />
                </div>
            ))}

            <h4>EQ</h4>

            {EQ_LABELS.map(({ key, label }) => (
                <div key={key} style={{
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '24px'
                }}>
                    {/* Gain ノブ */}
                    <div style={{ textAlign: 'center' }}>
                        <strong>{label}</strong>
                        <Knob
                            value={params.EQ_UI[key].gain}
                            min={-12}
                            max={12}
                            step={0.1}
                            onChange={value => updateEQ(key, 'gain', value)}
                            size={80}
                            color={
                                EQ_COLORS[key]
                            } 
                        />
                        <div>Gain: {params.EQ_UI[key].gain.toFixed(1)} dB</div>
                    </div>

                    {/* Lowcut / Highcut スライダー */}
                    <div style={{ flexGrow: 1 }}>
                        <div>
                            <label>Lowcut (Hz): {params.EQ_UI[key].lowcut}</label>
                            <input
                                type="range"
                                min={20}
                                max={500}
                                step={1}
                                value={params.EQ_UI[key].lowcut}
                                onChange={e => updateEQ(key, 'lowcut', +e.target.value)}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ marginTop: '8px' }}>
                            <label>Highcut (Hz): {params.EQ_UI[key].highcut}</label>
                            <input
                                type="range"
                                min={1000}
                                max={20000}
                                step={1}
                                value={params.EQ_UI[key].highcut}
                                onChange={e => updateEQ(key, 'highcut', +e.target.value)}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                </div>
            ))}
            <EQXYCanvas
                bands={eqBands}
                setBands={setEqBands}
                eq_color={EQ_COLORS}
            />
            <button
                onClick={() => setParams(DEFAULT_PARAMS)}
                style={{
                    marginTop: '20px',
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#ff4444',
                    color: '#fff',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                }}
            >
                🔄 Reset All
            </button>
        </div>
        
    );
};

export default AudioParamsForm;
